import { useMemo, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import axiosServices from "@pma/utils/axios"
import {
  ALL_MAIN_BOQ_ENDPOINT,
  BILLS_BY_BOQ_ID__LIST_ENDPOINT,
  FINANCIAL_SERVER_URL,
  PROJECTS_BY_CURRENT_USER_FILTER,
} from "@pma/utils/api-routes"
import { AnalyticsFilters } from "@pma/types"

export function useFinancialQueries(orgId: number | undefined, filters?: AnalyticsFilters) {
  const {
    data: projects,
    isLoading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useQuery<any[]>({
    queryKey: ["fin-projects", orgId],
    queryFn: async () => {
      const res = await axiosServices.post(PROJECTS_BY_CURRENT_USER_FILTER, {
        paging_start_page: 1,
        paging_per_page: 100,
        by_organization_id: orgId,
      })
      return res.data?.items || res.data || []
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const projectIds = useMemo(() => {
    const all = (projects || []).map((p: any) => p.id)
    if (filters?.projectIds?.length) {
      return all.filter((id: number) => filters.projectIds.includes(id))
    }
    return all
  }, [projects, filters?.projectIds])

  const {
    data: allBoqs,
    isLoading: boqsLoading,
    error: boqsError,
    refetch: refetchBoqs,
  } = useQuery<any[]>({
    queryKey: ["fin-boqs", orgId, projectIds],
    queryFn: async () => {
      if (!projectIds.length) return []
      const results = await Promise.allSettled(
        projectIds.map(async (pid: number) => {
          const res = await axiosServices.post(FINANCIAL_SERVER_URL + ALL_MAIN_BOQ_ENDPOINT, {
            organization_id: orgId,
            project_id: pid,
            show_deleted: false,
          })
          return { projectId: pid, boqs: res.data }
        }),
      )
      const boqs: any[] = []
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          const d = r.value
          const items = Array.isArray(d.boqs) ? d.boqs : d.boqs?.items || []
          items.forEach((boq: any) => boqs.push({ ...boq, _projectId: d.projectId }))
        }
      })
      return boqs
    },
    enabled: !!orgId && projectIds.length > 0,
    staleTime: 60_000,
  })

  const {
    data: allBills,
    isLoading: billsLoading,
    error: billsError,
    refetch: refetchBills,
  } = useQuery<any[]>({
    queryKey: ["fin-bills", orgId, allBoqs?.map((b) => b.id).join(",")],
    queryFn: async () => {
      if (!allBoqs?.length) return []
      const results = await Promise.allSettled(
        allBoqs.map(async (boq: any) => {
          const res = await axiosServices.post(FINANCIAL_SERVER_URL + BILLS_BY_BOQ_ID__LIST_ENDPOINT, {
            organization_id: orgId,
            boq_id: boq.id,
          })
          return { boqId: boq.id, bills: res.data }
        }),
      )
      const bills: any[] = []
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          const bd = r.value
          const items = Array.isArray(bd.bills) ? bd.bills : bd.bills?.items || []
          items.forEach((bill: any) => bills.push({ ...bill, _boqId: bd.boqId }))
        }
      })
      return bills
    },
    enabled: !!orgId && (allBoqs?.length || 0) > 0,
    staleTime: 60_000,
  })

  const isLoading = projectsLoading || boqsLoading || billsLoading
  const isError = projectsError || boqsError || billsError

  const refetchAll = useCallback(() => {
    refetchProjects()
    refetchBoqs()
    refetchBills()
  }, [refetchProjects, refetchBoqs, refetchBills])

  return {
    projects,
    allBoqs,
    allBills,
    isLoading,
    isError,
    refetchAll,
  }
}
